import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import BoundingBoxOverlay, {
  BoundingBox,
  BoxClickAnchor,
} from "./BoundingBoxOverlay";
import { FrameLabel } from "../common/ObjectTypes";
import {
  basePointAt,
  identityTransform,
  panBy,
  Transform,
  zoomAtPoint,
} from "./imageCanvasTransform";

const LOUPE_SIZE = 180; // px diameter
const LOUPE_ZOOM = 2.5;
const WHEEL_ZOOM_STEP = 1.1;
const BUTTON_ZOOM_STEP = 1.4;

/** Imperative controls exposed to an external toolbar (see FullScreenImageReview). */
export interface ImageCanvasHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  resetView: () => void;
}

interface ImageCanvasProps {
  imageUrl: string;
  imageAlt?: string;
  boxes: BoundingBox[];
  frameLabels?: FrameLabel[];
  selectedBoxId?: string | null;
  // AKL-698: forwards through to BoundingBoxOverlay's `highlightedBoxIds` so
  // the card ↔ bbox linkage highlight can render without touching popover state.
  highlightedBoxIds?: string[];
  // AKL-698: forwards through to BoundingBoxOverlay's `showLabels`.
  showLabels?: boolean;
  // AKL-717: the anchor is a mouse event or, for a keyboard activation, the
  // box's centre — see `BoxClickAnchor`.
  onBoxClick?: (boxId: string | null, anchor: BoxClickAnchor) => void;
  // Reports the current zoom as a whole-number percentage (100 = fit) so a
  // caller (e.g. the review top bar) can display it.
  onZoomChange?: (percent: number) => void;
  // Controlled magnifier state (the toggle button lives in the external tool
  // rail so it isn't overlaid on the image). When true, a hover loupe follows
  // the cursor over the image.
  magnifierOn?: boolean;
  // Optional CSS `filter` chain (e.g. "brightness(1.5) url(#akili-sharpen)")
  // applied to the underlying <img> only, so it stacks with the zoom transform
  // and does NOT affect box overlay coordinates or the magnifier loupe (which
  // renders raw pixels via a separate background-image on purpose).
  imageFilter?: string;
}

/**
 * Shared image viewport for the photo-review surfaces (AKL-681): renders the
 * image + bounding-box overlay with zoom, pan, and a hover magnifier. The
 * zoom/pan CSS transform is applied to a wrapper containing BOTH the image and
 * the overlay, so boxes track the image exactly; `BoundingBoxOverlay`'s
 * hit-test is transform-aware so clicks stay accurate under zoom. The zoom/reset
 * controls are driven imperatively (via ref) by an external tool rail, and the
 * loupe is scoped to this viewport so hovering the rail restores a normal cursor.
 */
const ImageCanvas = forwardRef<ImageCanvasHandle, ImageCanvasProps>(
  (
    {
      imageUrl,
      imageAlt = "",
      boxes,
      frameLabels,
      selectedBoxId,
      highlightedBoxIds,
      showLabels,
      onBoxClick,
      onZoomChange,
      magnifierOn = false,
      imageFilter,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const onZoomChangeRef = useRef(onZoomChange);
    onZoomChangeRef.current = onZoomChange;
    const [transform, setTransform] = useState<Transform>(identityTransform());
    const [dims, setDims] = useState({ width: 0, height: 0 });
    const [loupe, setLoupe] = useState<{
      visible: boolean;
      cx: number;
      cy: number;
    }>({ visible: false, cx: 0, cy: 0 });

    // Refs mirror the latest state for the native (non-passive) wheel handler,
    // which is registered once and must not close over stale values.
    const transformRef = useRef(transform);
    const dimsRef = useRef(dims);
    transformRef.current = transform;
    dimsRef.current = dims;

    const dragState = useRef<{
      dragging: boolean;
      startX: number;
      startY: number;
      moved: boolean;
      pointerId: number;
      captured: boolean;
    }>({
      dragging: false,
      startX: 0,
      startY: 0,
      moved: false,
      pointerId: -1,
      captured: false,
    });

    // Reset the view whenever the image changes (e.g. prev/next navigation).
    useEffect(() => {
      setTransform(identityTransform());
      setLoupe((l) => ({ ...l, visible: false }));
    }, [imageUrl]);

    // Report the current zoom percentage to the caller (for the top-bar readout).
    useEffect(() => {
      onZoomChangeRef.current?.(Math.round(transform.scale * 100));
    }, [transform.scale]);

    // Track the container size for translation clamping.
    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const update = () =>
        setDims({ width: el.clientWidth, height: el.clientHeight });
      update();
      if (typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
      }
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }, []);

    // Non-passive wheel listener so we can preventDefault the page scroll while
    // zooming to the cursor.
    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const onWheel = (e: WheelEvent) => {
        e.preventDefault();
        const rect = el.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        const factor = e.deltaY < 0 ? WHEEL_ZOOM_STEP : 1 / WHEEL_ZOOM_STEP;
        const d = dimsRef.current;
        setTransform(
          zoomAtPoint(transformRef.current, factor, cx, cy, d.width, d.height),
        );
      };
      el.addEventListener("wheel", onWheel, { passive: false });
      return () => el.removeEventListener("wheel", onWheel);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        zoomIn: () =>
          setTransform((t) =>
            zoomAtPoint(
              t,
              BUTTON_ZOOM_STEP,
              dims.width / 2,
              dims.height / 2,
              dims.width,
              dims.height,
            ),
          ),
        zoomOut: () =>
          setTransform((t) =>
            zoomAtPoint(
              t,
              1 / BUTTON_ZOOM_STEP,
              dims.width / 2,
              dims.height / 2,
              dims.width,
              dims.height,
            ),
          ),
        resetView: () => setTransform(identityTransform()),
      }),
      [dims],
    );

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
      if (transform.scale <= 1) return; // panning only when zoomed
      dragState.current = {
        dragging: true,
        startX: e.clientX,
        startY: e.clientY,
        moved: false,
        pointerId: e.pointerId,
        captured: false,
      };
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (magnifierOn && rect) {
        setLoupe({
          visible: true,
          cx: e.clientX - rect.left,
          cy: e.clientY - rect.top,
        });
      }
      if (!dragState.current.dragging) return;
      const dx = e.clientX - dragState.current.startX;
      const dy = e.clientY - dragState.current.startY;
      // Ignore sub-threshold jitter so an intended click doesn't nudge the image
      // (and stays a click, not a drag). Only pan once the drag threshold is met.
      if (!dragState.current.moved) {
        if (Math.abs(dx) <= 2 && Math.abs(dy) <= 2) return;
        dragState.current.moved = true;
        // Capture the pointer once a real drag begins so panning keeps tracking
        // even if the cursor leaves the image. Only after the threshold, so a
        // plain click (which never captures) still reaches the boxes.
        try {
          containerRef.current?.setPointerCapture(dragState.current.pointerId);
          dragState.current.captured = true;
        } catch {
          // setPointerCapture can throw if the pointer is already gone; ignore.
        }
      }
      dragState.current.startX = e.clientX;
      dragState.current.startY = e.clientY;
      setTransform((prev) => panBy(prev, dx, dy, dims.width, dims.height));
    };

    const endDrag = () => {
      if (dragState.current.dragging) {
        dragState.current.dragging = false;
        if (dragState.current.captured) {
          try {
            containerRef.current?.releasePointerCapture(
              dragState.current.pointerId,
            );
          } catch {
            // Pointer capture may already be released; ignore.
          }
          dragState.current.captured = false;
        }
      }
    };

    const handleBoxClick = (boxId: string | null, event: BoxClickAnchor) => {
      // Suppress the click that ends a pan drag so panning doesn't select a box.
      // AKL-717 (Copilot, web#775 round 6): a keyboard / assistive-technology
      // activation is never that click, so it consumes a stale flag (a pan that
      // ended without a click leaves it set) and still goes through.
      if (dragState.current.moved) {
        dragState.current.moved = false;
        if (!event.viaKeyboard) return;
      }
      onBoxClick?.(boxId, event);
    };

    // Loupe background math: measure the actually-rendered (contain-fitted,
    // centered) image rect live and magnify around the base-space point under the
    // cursor, inverting the current zoom/pan so it stays accurate at any zoom.
    const loupeStyle = (): React.CSSProperties | null => {
      const container = containerRef.current;
      const img = container?.querySelector("img");
      if (!container || !img) return null;
      const cr = container.getBoundingClientRect();
      const ir = img.getBoundingClientRect();
      // Undo the current transform to get the image's untransformed layout rect
      // (relative to the container), so the magnifier maps to the real pixels.
      const imgX = (ir.left - cr.left - transform.tx) / transform.scale;
      const imgY = (ir.top - cr.top - transform.ty) / transform.scale;
      const imgW = ir.width / transform.scale;
      const imgH = ir.height / transform.scale;
      const base = basePointAt(transform, loupe.cx, loupe.cy);
      const inImgX = base.x - imgX;
      const inImgY = base.y - imgY;
      return {
        position: "absolute",
        left: loupe.cx - LOUPE_SIZE / 2,
        top: loupe.cy - LOUPE_SIZE / 2,
        width: LOUPE_SIZE,
        height: LOUPE_SIZE,
        borderRadius: "50%",
        border: "2px solid rgba(255,255,255,0.9)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
        backgroundImage: `url(${imageUrl})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: `${imgW * LOUPE_ZOOM}px ${imgH * LOUPE_ZOOM}px`,
        backgroundPositionX: LOUPE_SIZE / 2 - inImgX * LOUPE_ZOOM,
        backgroundPositionY: LOUPE_SIZE / 2 - inImgY * LOUPE_ZOOM,
        pointerEvents: "none",
        zIndex: 20,
      };
    };

    // Contain the image within the viewport (px caps, so the whole image is
    // always visible at 100% — never clipped by the overflow-hidden container).
    const imageStyle: React.CSSProperties = {
      maxWidth: dims.width ? `${dims.width}px` : "100%",
      maxHeight: dims.height ? `${dims.height}px` : "100%",
      width: "auto",
      height: "auto",
    };
    const loupeCss = magnifierOn && loupe.visible ? loupeStyle() : null;

    return (
      <div
        ref={containerRef}
        data-testid="image-canvas"
        className="relative h-full w-full select-none overflow-hidden"
        style={{
          cursor:
            transform.scale > 1
              ? dragState.current.dragging
                ? "grabbing"
                : "grab"
              : magnifierOn
                ? "crosshair"
                : "default",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={() => {
          // Touch-gesture interruption / capture loss: clear the drag so the
          // grab cursor and loupe don't get stuck.
          endDrag();
          setLoupe((l) => ({ ...l, visible: false }));
        }}
        onPointerLeave={() => {
          endDrag();
          setLoupe((l) => ({ ...l, visible: false }));
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transform: `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})`,
            transformOrigin: "0 0",
          }}
        >
          <BoundingBoxOverlay
            imageUrl={imageUrl}
            imageAlt={imageAlt}
            boxes={boxes}
            frameLabels={frameLabels}
            frameLabelSize="large"
            selectedBoxId={selectedBoxId}
            highlightedBoxIds={highlightedBoxIds}
            showLabels={showLabels}
            onBoxClick={handleBoxClick}
            labelScale={1 / transform.scale}
            style={imageStyle}
            imageFilter={imageFilter}
          />
        </div>

        {loupeCss && <div data-testid="image-canvas-loupe" style={loupeCss} />}
      </div>
    );
  },
);

ImageCanvas.displayName = "ImageCanvas";

export default ImageCanvas;
